import {DateTime} from 'luxon'
import {FC, useEffect, useState} from 'react'

import {Modal} from '@/components/common/modal/Modal'
import {analytics} from '@/utils/tracking/analytics'
import {AuthActionType} from '@/utils/user/constants'

import {AccountActivationModal} from './AccountActivationModal'
import {MagicLinkSentModal} from './MagicLinkSentModal'
import {SignInModal} from './SignInModal'
import {SignUpModal} from './SignUpModal'

type AuthModalProps = {
  closeModal: () => void
  authActionType: AuthActionType
  setActiveAuthActionType?: (type: AuthActionType) => void
  onSuccessfulSignIn?: () => void
  hasSignUp?: boolean
  isVisible: boolean
}

export const AuthModal: FC<AuthModalProps> = ({
  closeModal,
  authActionType,
  setActiveAuthActionType,
  onSuccessfulSignIn,
  hasSignUp = true,
  isVisible,
}) => {
  const [internalAuthType, setInternalAuthType] = useState<AuthActionType>(authActionType)

  // Sync with prop when opening the modal (reset each time it's shown)
  useEffect(() => {
    if (!setActiveAuthActionType && isVisible) {
      setInternalAuthType(authActionType)
    }
  }, [isVisible, authActionType, setActiveAuthActionType])

  const activeAuthActionType = setActiveAuthActionType ? authActionType : internalAuthType
  const updateAuthType = setActiveAuthActionType ?? setInternalAuthType

  const [email, setEmail] = useState<string>('')
  const [magicLinkExpiresAt, setMagicLinkExpiresAt] = useState<DateTime | null>(null)

  useEffect(() => {
    if (isVisible) {
      analytics.track('Login modal viewed')
    }
  }, [isVisible])

  const closeMagicLinkSentModal = () => {
    setMagicLinkExpiresAt(null)
    closeModal()
  }

  const isActivation = activeAuthActionType === AuthActionType.activate

  return (
    <>
      <Modal isVisible={isVisible && !isActivation && !magicLinkExpiresAt} closeModal={closeModal}>
        {activeAuthActionType === AuthActionType.signUp && (
          <SignUpModal
            closeModal={closeModal}
            setActiveAuthActionType={updateAuthType}
            setEmail={setEmail}
          />
        )}
        {activeAuthActionType === AuthActionType.signIn && (
          <SignInModal
            closeModal={closeModal}
            setActiveAuthActionType={updateAuthType}
            onSuccessfulSignIn={onSuccessfulSignIn}
            setEmail={setEmail}
            setMagicLinkExpiresAt={setMagicLinkExpiresAt}
            hasSignUp={hasSignUp}
          />
        )}
      </Modal>
      <AccountActivationModal
        email={email}
        closeModal={closeModal}
        isVisible={isVisible && isActivation}
      />
      <MagicLinkSentModal
        email={email}
        closeModal={closeMagicLinkSentModal}
        isVisible={isVisible && magicLinkExpiresAt !== null}
        magicLinkExpiresAt={magicLinkExpiresAt}
      />
    </>
  )
}
